import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  TagIcon,
  CalendarIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon
} from '@heroicons/react/24/outline';

const ImagePreview = ({
  images = [],
  currentIndex = 0,
  isOpen,
  onClose,
  onNavigate,
  className = ''
}) => {
  const [zoom, setZoom] = useState(120);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [showInfo, setShowInfo] = useState(false);

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const currentImage = images[currentIndex];

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) {
            onNavigate(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (currentIndex < images.length - 1) {
            onNavigate(currentIndex + 1);
          }
          break;
        case '+':
        case '=':
          setZoom(prev => Math.min(prev + 25, 300));
          break;
        case '-':
          setZoom(prev => Math.max(prev - 25, 25));
          break;
        case '0':
          setZoom(120);
          setImagePosition({ x: 0, y: 0 });
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  // Resim değiştiğinde zoom'u sıfırla
  useEffect(() => {
    setZoom(120);
    setImagePosition({ x: 0, y: 0 });
  }, [currentIndex]);

  // Mouse wheel ile zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -25 : 25;
    setZoom(prev => Math.max(25, Math.min(300, prev + delta)));
  };

  // Mouse drag ile görsel taşıma
  const handleMouseDown = (e) => {
    if (zoom > 100) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 100) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Dosya indirme
  const handleDownload = () => {
    if (!currentImage) return;

    const link = document.createElement('a');
    link.href = currentImage.preview || currentImage.file_path || currentImage.optimized_url || currentImage.thumbnail_url;
    link.download = currentImage.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dosya boyutu formatı
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Tarih formatı
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !currentImage) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[10001] outline-none focus:outline-none overflow-hidden animate-in fade-in duration-300">
      <div className={`relative w-full h-full flex flex-col ${className}`}>
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-white font-medium">
                {currentImage.name}
              </h2>
              <span className="text-gray-300 text-sm">
                {currentIndex + 1} / {images.length}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              {/* Zoom Controls */}
              <div className="flex items-center space-x-1 bg-black/50 rounded-lg p-1">
                <button
                  onClick={() => setZoom(prev => Math.max(prev - 25, 25))}
                  className="p-1 text-white hover:bg-white/20 rounded"
                  title="Küçült (-)"
                >
                  <MagnifyingGlassMinusIcon className="w-4 h-4" />
                </button>
                <span className="text-white text-sm px-2 min-w-[3rem] text-center">
                  {zoom}%
                </span>
                <button
                  onClick={() => setZoom(prev => Math.min(prev + 25, 300))}
                  className="p-1 text-white hover:bg-white/20 rounded"
                  title="Büyüt (+)"
                >
                  <MagnifyingGlassPlusIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Download */}
              <button
                onClick={handleDownload}
                className="p-2 text-white hover:bg-white/20 rounded-lg"
                title="İndir"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
              </button>

              {/* Close */}
              <button
                onClick={onClose}
                className="p-2 text-white hover:bg-white/20 rounded-lg"
                title="Kapat (Esc)"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center relative">
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={() => currentIndex > 0 && onNavigate(currentIndex - 1)}
                disabled={currentIndex === 0}
                className="absolute left-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Önceki görsel (←)"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>

              <button
                onClick={() => currentIndex < images.length - 1 && onNavigate(currentIndex + 1)}
                disabled={currentIndex === images.length - 1}
                className="absolute right-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Sonraki görsel (→)"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Image Container */}
          <div
            className="flex items-center justify-center w-full h-full p-2"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoom > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={currentImage.preview || currentImage.file_path || currentImage.optimized_url || currentImage.thumbnail_url}
              alt={currentImage.name}
              className="w-auto h-auto max-w-none max-h-none object-contain"
              style={{
                maxWidth: '95vw',
                maxHeight: '85vh',
                minWidth: '60vw',
                minHeight: '40vh',
                transform: `scale(${zoom / 100}) translate(${imagePosition.x / (zoom / 100)}px, ${imagePosition.y / (zoom / 100)}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              draggable={false}
            />
          </div>
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="flex space-x-2 bg-black/50 rounded-lg p-2 max-w-md overflow-x-auto">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => onNavigate(index)}
                  className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${index === currentIndex
                      ? 'border-white'
                      : 'border-transparent hover:border-gray-400'
                    }`}
                >
                  <img
                    src={image.preview || image.file_path || image.optimized_url || image.thumbnail_url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
};

export default ImagePreview;
